// M4 workstream C (world) player actions — re-exported through src/core/actions.js (names unique across workstreams).
// Each returns { ok, msg }.
import { fieldDef } from '../defs.js';
import { popDecision } from '../security.js';
import { plotWater } from '../m3/opsutil.js';
import { releaseCharterImpl } from '../m3/charters.js';
import { m4On, regionOf } from './worldCore.js';
import { permitRule, permitFor, filePermit, moratoriumAt } from './worldRegime.js';
import { installSubseaProcessingImpl } from './worldDeep.js';
import { buyMegaShareImpl } from './worldMega.js';
import { divestRegionImpl, resolveM4NatImpl } from './worldPolitics.js';
import { buildOilSandsUnitImpl, openMineBlockImpl, treatTailingsImpl } from './worldOilSands.js';

const no = (msg) => ({ ok: false, msg });

// §11: file a deepwater drilling permit ahead of the rig (Oct 2010 – Sept 2011 slowdown: 90–200 days).
export function fileDrillingPermit(state, fieldId, plotIdx) {
  if (!m4On(state)) return no('No permit is needed.');
  const p = state.fields[fieldId]?.plots?.[plotIdx];
  if (!p) return no('No such plot.');
  if (!p.owned) return no('Lease the block first.');
  const rule = permitRule(state, regionOf(fieldId), plotWater(fieldId, plotIdx));
  if (!rule) return no('No special permit is required here today.');
  const had = permitFor(state, fieldId, plotIdx);
  const rec = had ?? filePermit(state, fieldId, plotIdx, rule);
  return { ok: true, msg: had ? `Permit already filed: approval expected in ${Math.max(0, rec.readyDay - state.day)} days.` : `Permit filed for ${fieldDef(fieldId)?.name} block ${plotIdx}: approval expected in ${rec.days} days.` };
}

// §11: release a chartered rig without the early-termination fee while a moratorium stops it (force majeure).
export function forceMajeureCharter(state, wellId) {
  const w = state.wells?.[wellId];
  const c = w?.contract?.charter;
  if (!c || c.closed) return no('No chartered rig on that well.');
  const mor = moratoriumAt(state, regionOf(w.fieldId), w.waterDepthFt ?? 0);
  if (!mor?.forceMajeure) return no('Force majeure applies only while a moratorium stops the work.');
  c.minDays = Math.min(c.minDays, c.billedDays); // no minimum-term balance, no early-termination fee
  c.forceMajeure = state.day;
  const r = releaseCharterImpl(state, wellId);
  return r.ok ? { ok: true, msg: `Force majeure declared: the charter on ${w.name} ends with no termination fee.` } : r;
}

// §10: subsea boosting / separation on a tied-back well.
export const installSubseaProcessing = (state, wellId, defId = 'subsea_boosting') => installSubseaProcessingImpl(state, wellId, defId);
// §10: buy a share of a 2000s megaproject (MEGAPROJECTS_M4) at FID.
export const buyMegaprojectShare = (state, id, share) => buyMegaShareImpl(state, id, share);
// §12: sell out of a sanctioned region before its divest-by date.
export const divestRegion = (state, regionId) => divestRegionImpl(state, regionId);
// §15: oil-sands plants & contracts at a field (mine fleet, extraction, froth, upgrader, steam plant, solvent kit, diluent, tailings).
export const buildOilSandsUnit = (state, fieldId, defId) => buildOilSandsUnitImpl(state, fieldId, defId);
export const openMineBlock = (state, fieldId, plotIdx) => openMineBlockImpl(state, fieldId, plotIdx);
export const treatTailings = (state, fieldId, amount = null) => treatTailingsImpl(state, fieldId, amount);

// core/actions.resolveDecision hunk: Venezuela 2007-style "sign or be seized" decisions.
export const M4W_DECISION_KINDS = new Set(['m4Nationalization']);
export function resolveM4WorldDecision(state, d, choice) {
  if (d.kind === 'm4Nationalization') {
    if (!['accept', 'refuse'].includes(choice)) return no('Choose accept or refuse.');
    return resolveM4NatImpl(state, d, choice);
  }
  popDecision(state, d.id);
  return { ok: true, msg: 'Dismissed.' };
}
